import React from 'react';
import type { ComponentType, ReactNode } from 'react';

import ButtonImpl from 'components/partials/button/button';

interface ButtonLikeProps {
  children?: ReactNode;
  variant?: string;
  size?: string;
  onClick?: (
    event: React.MouseEvent<HTMLButtonElement> &
      React.KeyboardEvent<HTMLButtonElement>
  ) => void;
  'data-testid'?: string;
}

const Button = ButtonImpl as unknown as ComponentType<ButtonLikeProps>;

import { isApplePlatform, isSafari } from 'helpers/utils/screenshot-core';
import {
  BAR_TOP,
  BAR_HEIGHT,
  LEFT_OFFSET,
  WIDTH_OFFSET,
  SAFARI_OFFSET,
} from 'helpers/utils/components/constants';

export interface SnappingRect {
  top: number;
  left: number;
  width: number;
  height: number;
  right: number;
  bottom: number;
}

export interface SnappingBarProps {
  rect: SnappingRect;
  onCancel: (event?: React.MouseEvent | React.KeyboardEvent) => void;
  onPasteClick?: ((event: React.MouseEvent<HTMLButtonElement>) => void) | null;
}

const SnappingBar: React.FC<SnappingBarProps> = ({
  rect,
  onCancel,
  onPasteClick,
}) => {
  const safariMode = isSafari();

  return (
    <div
      className="fixed z-[10002]"
      style={{
        left: rect.left - LEFT_OFFSET,
        width: rect.width + WIDTH_OFFSET,
        top: BAR_TOP,
      }}
    >
      <div
        className="bg-white px-4 flex items-center justify-between"
        style={{ height: safariMode ? BAR_HEIGHT + SAFARI_OFFSET : BAR_HEIGHT }}
      >
        <div>
          <p className="font-bold text-gray-700">How to take a screenshot</p>
          <p className="text-sm text-gray-600">
            {safariMode ? (
              <>
                1. Press{' '}
                <span className="font-bold">
                  {isApplePlatform()
                    ? 'Cmd + Ctrl + Shift +4'
                    : 'Win + Shift + S (or Print Screen)'}
                </span>{' '}
                to take a screenshot
                <br />
                2. Click "Paste Screenshot" below to load it from clipboard
              </>
            ) : (
              <>
                Press{' '}
                <span className="font-bold">
                  {isApplePlatform()
                    ? 'Cmd + Ctrl + Shift +4'
                    : 'Win + Shift + S (or Print Screen)'}
                </span>{' '}
                to take a screenshot to copy it to the clipboard. The
                instruction window will close automatically and will not appear
                in the screenshot
              </>
            )}
          </p>
        </div>
        <div className="flex flex-col gap-2">
          {safariMode && (
            <Button
              variant="brand-filled"
              size="sm"
              onClick={onPasteClick ?? undefined}
              data-testid="paste-screenshot-btn"
            >
              Paste Screenshot
            </Button>
          )}
          <Button
            variant="reserve-outlined"
            size="sm"
            onClick={onCancel}
            data-testid="cancel-snapping-bar-button"
          >
            Cancel snapping
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SnappingBar;
